import { useState } from 'react';

export function Settings() {
  const [printfulStoreId, setPrintfulStoreId] = useState('');
  const [printfulApiKey, setPrintfulApiKey] = useState('');
  const [printfulConnected, setPrintfulConnected] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [defaultMarkup, setDefaultMarkup] = useState(120);
  const [minMargin, setMinMargin] = useState(30);
  const [brandDefaults, setBrandDefaults] = useState({
    Calitoy: { markup: 130, colors: 'Black, Gold, Wine' },
    Kurced: { markup: 110, colors: 'Black, Wine' },
    CalitoyMuse: { markup: 140, colors: 'Gold, White' },
  });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [saved, setSaved] = useState(false);

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  function testPrintful() {
    if (printfulApiKey.length > 0) {
      setPrintfulConnected(true);
    }
  }

  function testStripe() {
    if (stripeKey.length > 0) {
      setStripeConnected(true);
    }
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <h1 style={{ marginBottom: '24px' }}>Settings</h1>

      {/* Printful */}
      <div className="panel">
        <div className="panel__header">
          <h3 className="panel__title">Printful Store</h3>
          <span className={`status-badge ${printfulConnected ? 'status-badge--approved' : 'status-badge--draft'}`}>
            <span className="status-badge__dot" />
            {printfulConnected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="form-group">
          <label className="form-label">Printful Store ID</label>
          <input
            className="form-input"
            value={printfulStoreId}
            onChange={(e) => setPrintfulStoreId(e.target.value)}
            placeholder="e.g., 1234567"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Printful API Key</label>
          <input
            className="form-input"
            type="password"
            value={printfulApiKey}
            onChange={(e) => setPrintfulApiKey(e.target.value)}
            placeholder="Enter your Printful API key"
          />
        </div>
        <button className="btn btn--ghost" onClick={testPrintful}>
          Test Connection
        </button>
      </div>

      {/* Stripe */}
      <div className="panel">
        <div className="panel__header">
          <h3 className="panel__title">Stripe</h3>
          <span className={`status-badge ${stripeConnected ? 'status-badge--approved' : 'status-badge--draft'}`}>
            <span className="status-badge__dot" />
            {stripeConnected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        <div className="form-group">
          <label className="form-label">Stripe Secret Key</label>
          <input
            className="form-input"
            type="password"
            value={stripeKey}
            onChange={(e) => setStripeKey(e.target.value)}
            placeholder="sk_live_..."
          />
        </div>
        <button className="btn btn--ghost" onClick={testStripe}>
          Test Connection
        </button>
      </div>

      {/* Pricing Rules */}
      <div className="panel">
        <div className="panel__header">
          <h3 className="panel__title">Pricing Rules</h3>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Default Markup (%)</label>
            <input
              className="form-input"
              type="number"
              value={defaultMarkup}
              onChange={(e) => setDefaultMarkup(parseInt(e.target.value) || 0)}
            />
            <p className="form-hint">Added to Printful cost to calculate retail price.</p>
          </div>
          <div className="form-group">
            <label className="form-label">Minimum Margin (%)</label>
            <input
              className="form-input"
              type="number"
              value={minMargin}
              onChange={(e) => setMinMargin(parseInt(e.target.value) || 0)}
            />
            <p className="form-hint">Minimum acceptable margin. Jobs below this are flagged.</p>
          </div>
        </div>
      </div>

      {/* Brand Defaults */}
      <div className="panel">
        <div className="panel__header">
          <h3 className="panel__title">Brand Defaults</h3>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Markup (%)</th>
                <th>Default Colors</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(brandDefaults).map(([brand, defaults]) => (
                <tr
                  key={brand}
                  style={{ cursor: 'default' }}
                  onClick={() => {}}
                >
                  <td style={{ fontWeight: 600, color: 'var(--gold)' }}>{brand}</td>
                  <td>
                    <input
                      className="form-input"
                      type="number"
                      style={{ width: '80px' }}
                      value={defaults.markup}
                      onChange={(e) =>
                        setBrandDefaults((prev) => ({
                          ...prev,
                          [brand]: { ...prev[brand as keyof typeof prev], markup: parseInt(e.target.value) || 0 },
                        }))
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="form-input"
                      style={{ width: '200px' }}
                      value={defaults.colors}
                      onChange={(e) =>
                        setBrandDefaults((prev) => ({
                          ...prev,
                          [brand]: { ...prev[brand as keyof typeof prev], colors: e.target.value },
                        }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Webhooks */}
      <div className="panel">
        <div className="panel__header">
          <h3 className="panel__title">Webhook Endpoints</h3>
        </div>
        <div className="form-group">
          <label className="form-label">Webhook URL</label>
          <input
            className="form-input"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://your-app.com/api/webhooks/printful"
          />
          <p className="form-hint">Printful will send order status updates to this URL.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span className="pill">order.created</span>
          <span className="pill">order.updated</span>
          <span className="pill">package.shipped</span>
          <span className="pill">product.synced</span>
          <span className="pill">mockup.completed</span>
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '24px' }}>
        <button className="btn btn--primary btn--lg" onClick={handleSave}>
          Save Settings
        </button>
        {saved && (
          <span style={{ color: 'var(--green)', fontSize: '0.85rem' }}>
            Settings saved successfully.
          </span>
        )}
      </div>
    </div>
  );
}
